'use client';

import {
  lazy,
  Suspense,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';

import { useIsFunnelTracked } from '@/shared-measurement';
import {
  buildResponderCapabilitiesFromProject,
  hasUrlOrTaskResponderQuestion,
  isSpokenSession,
  ResponderGateLoading,
  ResponderV2Gate,
  toSupportedDeviceTypes,
} from '@/shared-responder-v2';
import {
  DEVICE_GATE_VERDICTS,
  resolveDeviceGate,
  SCREENER_STATUSES,
} from '@/shared-responder-v2-core';
import {
  createApolloResponderGateway,
  toServedResponderProjectInput,
  useResponderScreener,
  useSurveyEmbedSessionPort,
  useSurveySpeechSynthesisPort,
  useSurveySpeechToTextTokenPort,
  useSurveySpokenPickExtractionPort,
  useSurveyTranscriptFormattingPort,
  useSurveyVoiceRecordingPort,
  resolvePanelCompletionRedirect,
  type SurveyUnifiedProject,
} from '@/shared-responder-v2-gateway';
import { useResponderAudioSession } from '@/shared-responder-v2-media';
import { ResponderBrandContext } from '@/shared-responder-v2-shell';
import {
  getMobileUtScreenerSessionKey,
  getMobileUtUniqueId,
  isMobileUtMode,
  MobileUtPhoneShell,
  parsePanel,
  removeMobileUtScreenerSessionKey,
} from '@/shared-survey';
import { resolveProjectTitle, useViewportClass } from '@/shared-utilities';
import type { Channel } from '@/survey-graphql';

import { withResponderFunnelAnalytics } from './survey-responder-v2-adapter-funnel.decorator';
import { createFunnelCompletionPort } from './survey-responder-v2-adapter-funnel.helpers';
import {
  isMicrophoneAllowedByPermissionsPolicy,
  isMicrophoneAllowedOnServer,
  subscribeToNothing,
} from './survey-responder-v2-adapter-microphone-policy.helpers';
import { useSurveyMobileRelayPort } from './survey-responder-v2-adapter-mobile-relay.hooks';
import {
  MOBILE_UT_ENTRIES,
  resolveMobileUtEntry,
  supportsPhoneResponders,
} from './survey-responder-v2-adapter-mobile-ut-entry.helpers';
import { useResponderContact } from './survey-responder-v2-adapter-responder-contact.hooks';
import { useSurveyScreenRecordingPort } from './survey-responder-v2-adapter-screen-recording.hooks';

const SAME_ORIGIN_API_BASE_URL = '';

/*
 * Lazy for the same reason `ResponderV2` is inside the shared gate: the flow
 * renders the `-shell` screener components, and a screener-free project should
 * download none of them.
 */
const SurveyResponderV2AdapterScreenerFlow = lazy(
  () => import('./survey-responder-v2-adapter-screener-flow')
);

const SurveyResponderV2AdapterDeviceGate = lazy(
  () => import('./survey-responder-v2-adapter-device-gate')
);

interface Props {
  isEligible: boolean;
  project?: SurveyUnifiedProject | null;
  /**
   * `projectOrgSettings.font` from the same query as `project`, the rung the
   * responder resolves under the project's own font.
   */
  orgFont?: string | null;
  orgLogoUrl?: string | null;
  orgIconUrl?: string | null;
  /** False inside `/manager-embed`, where mic access is blocked. */
  allowVoice?: boolean;
  /**
   * The channel the route resolved server-side, used when the link carries no
   * `?channel=`. It must match the one `hasScreener` was queried with: the
   * screener issues its key against this channel and
   * `initiateUnifiedSession` spends it against the same one.
   */
  channel?: Channel;
  isExpired?: boolean;
  /** `initiateSurveyV3`'s session, forwarded to the phone shell as data only. */
  sessionId?: string | null;
  /**
   * Whether this route may hand a phone off to the VM. Only the direct project
   * link opts in; the embeds and the free survey always answer in place.
   */
  allowsPhoneHandoff?: boolean;
  children: ReactNode;
}

/**
 * The survey app's entry into the responder: the only place that knows both
 * the survey schema and Apollo. It maps the project onto the schema-free shape
 * the responder reads and injects the survey-backed gateway, so everything
 * below it is the same code the builder preview runs.
 *
 * The recording port is handed the same `<audio>` element the playback port
 * speaks through, which is what puts the assistant's voice into the saved
 * video rather than only the responder's. A responder without an `?email=` is
 * anonymous, and an anonymous session uploads no voice asset.
 *
 * The screener stands between this gate and the responder: a screener-gated
 * project renders the flow until it completes (or bypasses), and the key it
 * issues remounts the V2 tree so every fresh key starts a clean session. The
 * screener is survey-only, which is why it lives here and not in the shared
 * gate the builder preview also mounts. The device gate runs ahead of it:
 * the responder's own gate only renders once a session exists, which is after
 * the screener, so a respondent on the wrong device would otherwise answer the
 * screener only to be sent to another device to answer it again.
 *
 * It is also where mobile usability testing is decided, after the screener so
 * a gated project still screens first. Only the phone gets its own tree; a
 * page running inside the VM the phone drives needs no branch at all, because
 * the relay port carries the phone's id and everything downstream — the
 * skipped device gate, the remote microphone — follows from that one field.
 */
export function SurveyResponderV2Gate({
  isEligible,
  project = null,
  orgFont = null,
  orgLogoUrl = null,
  orgIconUrl = null,
  allowVoice = false,
  channel,
  isExpired = false,
  sessionId = null,
  allowsPhoneHandoff = false,
  children,
}: Props) {
  const searchParams = useSearchParams();

  const isMicrophoneAllowed = useSyncExternalStore(
    subscribeToNothing,
    isMicrophoneAllowedByPermissionsPolicy,
    isMicrophoneAllowedOnServer
  );

  const relayedScreenerKey = getMobileUtScreenerSessionKey(searchParams);

  const shouldRunScreener =
    isEligible &&
    !isExpired &&
    project?.hasScreener === true &&
    relayedScreenerKey === null;

  const client = useApolloClient();

  const posthog = usePostHog();
  const isFunnelTracked = useIsFunnelTracked();

  const audio = useResponderAudioSession();
  const contact = useResponderContact(channel);
  const embed = useSurveyEmbedSessionPort(SAME_ORIGIN_API_BASE_URL);
  const speechToText = useSurveySpeechToTextTokenPort();
  const spokenPick = useSurveySpokenPickExtractionPort();
  const synthesis = useSurveySpeechSynthesisPort(project?.id);
  const transcriptFormatting = useSurveyTranscriptFormattingPort();
  const voice = useSurveyVoiceRecordingPort(contact.email === null);
  const recording = useSurveyScreenRecordingPort(
    audio.element,
    voice.subscribeStream
  );
  const mobileRelay = useSurveyMobileRelayPort(
    isMobileUtMode(searchParams) ? getMobileUtUniqueId(searchParams) : null
  );

  const { viewportClass, mounted } = useViewportClass();

  /*
   * Inside the VM the viewport is not the device the responder holds, which is
   * why the responder skips its own gate there too. Until mount the viewport
   * reads as a phone whatever the device, so the screener waits for it rather
   * than starting on a guess.
   */
  const deviceGate =
    mobileRelay.uniqueId === null
      ? resolveDeviceGate({
          supportedDeviceTypes: toSupportedDeviceTypes(project?.deviceTypes),
          viewportClass,
        })
      : DEVICE_GATE_VERDICTS.Allowed;
  const isDeviceBlocked =
    mounted && deviceGate !== DEVICE_GATE_VERDICTS.Allowed;

  const panel = useMemo(() => parsePanel(searchParams), [searchParams]);

  const screener = useResponderScreener({
    client,
    projectId: project?.id ?? '',
    channel: contact.channel,
    panel,
    hasScreener: shouldRunScreener,
    isDeferred: !mounted || isDeviceBlocked,
  });

  const funnel = { posthog, projectId: project?.id, isFunnelTracked };

  const gateway = useMemo(
    () =>
      withResponderFunnelAnalytics(
        createApolloResponderGateway(client, panel),
        { posthog, projectId: project?.id, isFunnelTracked }
      ),
    [client, panel, posthog, project?.id, isFunnelTracked]
  );

  const capabilities = {
    completion: createFunnelCompletionPort(funnel),
    embed,
    gateway,
    mobileRelay,
    playback: audio.playback,
    recording,
    speechToText,
    spokenPick,
    synthesis,
    transcriptFormatting,
    voice,
  };

  const responderProject =
    project === null
      ? null
      : {
          ...toServedResponderProjectInput(project, orgFont, contact),
          completionRedirectUrl: resolvePanelCompletionRedirect(panel),
        };

  const isVoiceAllowed = allowVoice && isMicrophoneAllowed;

  const isScreenerPending =
    shouldRunScreener &&
    screener.state.status !== SCREENER_STATUSES.Completed &&
    screener.state.status !== SCREENER_STATUSES.Bypassed;

  function renderGate() {
    /*
     * Only while the screener is still ahead: past it, the responder's own gate
     * owns the device check, and swapping the tree out here would discard the
     * session in progress.
     */
    if (isScreenerPending && isDeviceBlocked) {
      return (
        <Suspense fallback={<ResponderGateLoading />}>
          <SurveyResponderV2AdapterDeviceGate verdict={deviceGate} />
        </Suspense>
      );
    }

    if (isScreenerPending) {
      return (
        <Suspense fallback={<ResponderGateLoading />}>
          <SurveyResponderV2AdapterScreenerFlow
            onContinue={screener.onContinue}
            onToggleOption={screener.onToggleOption}
            state={screener.state}
          />
        </Suspense>
      );
    }

    const mobileUtEntry = resolveMobileUtEntry({
      isInsideVm: mobileRelay.uniqueId !== null,
      viewportClass,
      hasUrlOrTask:
        responderProject === null
          ? false
          : hasUrlOrTaskResponderQuestion(responderProject.questions),
      supportsPhone: supportsPhoneResponders(project?.deviceTypes),
    });

    const screenerSessionKey =
      screener.state.status === SCREENER_STATUSES.Completed
        ? screener.state.sessionKey
        : null;

    if (
      isEligible &&
      !isExpired &&
      project !== null &&
      allowsPhoneHandoff &&
      mobileUtEntry === MOBILE_UT_ENTRIES.PhoneHandoff
    ) {
      /*
       * The viewport is unknown until hydration, and `useViewportClass` reports
       * the narrowest class until then, so a computer would otherwise render one
       * frame of the phone shell — and open a Hyperbeam session for it.
       */
      if (!mounted) {
        return <ResponderGateLoading />;
      }

      const usesMicrophone =
        responderProject !== null &&
        isSpokenSession(
          buildResponderCapabilitiesFromProject(
            responderProject,
            isVoiceAllowed
          )
        );

      return (
        <MobileUtPhoneShell
          projectId={project.id}
          screenerSessionKey={screenerSessionKey}
          sessionId={sessionId}
          title={resolveProjectTitle(project) || 'User Test'}
          usesMicrophone={usesMicrophone}
        />
      );
    }

    const sessionKey = relayedScreenerKey ?? screenerSessionKey;

    /*
     * A relayed key the backend rejects (spent by an earlier load of this VM
     * page, or expired) sends the VM page through its own screener rather than
     * failing the session.
     */
    function onScreenerKeyRejected(): void {
      if (relayedScreenerKey === null) {
        screener.onKeyRejected();
        return;
      }

      window.location.replace(
        removeMobileUtScreenerSessionKey(window.location.href)
      );
    }

    return (
      <ResponderV2Gate
        key={sessionKey ?? 'no-screener'}
        allowVoice={isVoiceAllowed}
        capabilities={capabilities}
        contact={contact}
        isEligible={isEligible}
        isExpired={isExpired}
        onScreenerKeyRejected={onScreenerKeyRejected}
        project={responderProject}
        sessionKey={sessionKey}
      >
        {children}
      </ResponderV2Gate>
    );
  }

  return (
    <ResponderBrandContext value={{ logoUrl: orgLogoUrl, iconUrl: orgIconUrl }}>
      {renderGate()}
    </ResponderBrandContext>
  );
}
